import { MjmlColumn, MjmlRaw } from "@faire/mjml-react";
import { genericMemo } from "@redotech/react-util/component";
import {
  EmailBlockType,
  Section,
  SharedProps,
} from "@redotech/redo-model/email-builder";
import { useContext } from "react";
import { Flex } from "../../flex";
import { FormElementDrop } from "../../form-builder/form-element-drop";
import {
  WidgetEditorDecorator,
  WidgetProps,
} from "../../form-builder/widget-editors/widget-editor-decorator";
import { Text } from "../../text";
import { nestedEmailBlocks } from "../nested-email-blocks";
import { EmailBuilderContext } from "./email-builder-context";
import { generateExtraProps, makeObjectId } from "./preview";
import { defaultBlocks, editorPreviews } from "./shared-editor-types";

export const ColumnEditor = genericMemo(function ColumnEditor<
  T extends WidgetProps<Section.Column> & SharedProps,
>(props: WidgetEditorDecorator<T, Section.Column>) {
  const { element } = props.widget;
  const emailBuilderContext = useContext(EmailBuilderContext);
  const getProduct = emailBuilderContext?.getProduct;

  const setColumnContent = (columnIndex: number, newSection: Section) => {
    const newColumns = [...element.columns];
    newColumns[columnIndex] = newSection;
    props.setElement({ ...element, columns: newColumns });
  };

  return (
    <div
      onClick={(e) => {
        props.onSelectElement(props.index, e);
        props.setSelectedColumnIndex?.(undefined);
        e.stopPropagation();
      }}
      style={{
        position: props.selected ? "relative" : undefined,
        zIndex: props.selected ? 999 : undefined,
        cursor: "pointer",
        outline: props.selected ? "2px solid #4D35C6" : "none",
      }}
    >
      <div
        style={{
          display: "flex",
          padding: `${element.sectionPadding.top}px ${element.sectionPadding.right}px ${element.sectionPadding.bottom}px ${element.sectionPadding.left}px`,
          backgroundColor: element.sectionColor,
        }}
      >
        {Array.from({ length: Number(element.columnCount) }).map(
          (_, columnIndex) => {
            const column = element.columns[columnIndex];

            const BlockEditor = column
              ? editorPreviews[column.type]
              : undefined;

            return (
              <div
                key={columnIndex}
                style={{ width: `${100 / Number(element.columnCount)}%` }}
              >
                {BlockEditor ? (
                  <BlockEditor
                    {...props}
                    onSelectElement={(_, e) => {
                      props.setSelectedColumnIndex?.(columnIndex);
                      props.onSelectElement(props.index, e);
                      e.stopPropagation();
                    }}
                    sectionToolbar={undefined}
                    selected={
                      props.selectedColumnIndex === columnIndex &&
                      props.selected
                    }
                    setElement={(newSection) =>
                      setColumnContent(columnIndex, newSection)
                    }
                    widget={{
                      format: props.widget.format,
                      team: props.widget.team,
                      template: props.widget.template,
                      schemaInstance: props.widget.schemaInstance,
                      utm: props.widget.utm,
                      isBuilder: true,
                      element: column,
                      extraProps: getProduct
                        ? generateExtraProps(props.widget.element, getProduct)
                        : {},
                    }}
                  />
                ) : (
                  <FormElementDrop
                    elementDropped={(type) =>
                      setColumnContent(columnIndex, {
                        ...defaultBlocks[type],
                        blockId: makeObjectId(),
                      })
                    }
                    hideUntilDragging
                    possibleValues={Object.values(EmailBlockType).filter(
                      (type) => nestedEmailBlocks[type],
                    )}
                  >
                    <MjmlColumn key={columnIndex}>
                      <MjmlRaw>
                        <Flex
                          align="center"
                          justify="center"
                          m="lg"
                          style={{
                            backgroundColor: "#E5E5E5",
                            height: "160px",
                          }}
                          textAlign="center"
                        >
                          <Text fontSize="xs" style={{ width: "88px" }}>
                            Drop content into columns
                          </Text>
                        </Flex>
                      </MjmlRaw>
                    </MjmlColumn>
                  </FormElementDrop>
                )}
              </div>
            );
          },
        )}
      </div>

      {props.sectionToolbar}
    </div>
  );
});
